import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaArrowDown } from 'react-icons/fa';
import instanciaAxios from '../../miscelaneo/instanciaAxios';
import { useContextoSesion } from '../../miscelaneo/ContextoSesion';
import SpinnerCargando from '../util/SpinnerCargando';
import FuncionesGenerales from '../util/FuncionesGenerales';
import ModalRetiroCaja from './ModalRetiroCaja';

// Componente de Tarjeta de Resumen
function TarjetaResumen({ titulo, valor, colorBorde, colorTexto }) {
  return (
    <div className={`bg-white rounded-xl border shadow-md p-4 border-l-4 ${colorBorde}`}>
      <p className="text-sm text-gray-500">{titulo}</p>
      <p className={`text-2xl font-bold mt-1 ${colorTexto}`}>{valor}</p>
    </div>
  );
}

export default function ResumenMovimientos() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { sesionCaja, sesionCajaCargada, cargando: cargandoContexto } = useContextoSesion();
  const { usuario } = useContextoSesion();
  const { formatoMoneda } = FuncionesGenerales(usuario);

  const [resumen, setResumen] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [mostrarModalRetiro, setMostrarModalRetiro] = useState(false);

  useEffect(() => {
    if (cargandoContexto || !sesionCajaCargada) return;

    if (!sesionCaja) {
      toast.warning('No tienes una caja abierta');
      navigate('/AperturaCaja');
      return;
    }

    cargarResumen();
  }, [sesionCaja, sesionCajaCargada, cargandoContexto, navigate]);

  useEffect(() => {
    if (searchParams.get('retiro') === '1' && !cargando && resumen) {
      setMostrarModalRetiro(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, cargando, resumen, setSearchParams]);

  const cargarResumen = async () => {
    if (!sesionCaja?.id_sesion_caja) return;
    try {
      setCargando(true);
      const response = await instanciaAxios.get(`/sesion-caja/${sesionCaja.id_sesion_caja}/resumen`);
      setResumen(response.data);
    } catch (error) {
      console.error('Error al cargar resumen:', error);
      toast.error('Error al cargar el resumen de la sesión');
    } finally {
      setCargando(false);
    }
  };

  const formatMonto = (monto) => {
    return parseFloat(monto || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  if (cargandoContexto || !sesionCajaCargada || cargando) return <SpinnerCargando />;
  if (!sesionCaja) return null;
  if (!resumen) return <p className="text-center text-gray-500 py-8">No se pudo cargar el resumen</p>;

  return (
    <div className="bg-gray-200 min-h-screen">
      <div className="flex justify-center py-10">
        <div className="bg-gray-100 bg-opacity-90 rounded-xl border border-gray-100 p-12 w-full max-w-7xl mx-10">
          <div className="flex flex-col gap-8">
            {/* Header */}
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <h1 className="font-cascadia font-bold text-2xl">Resumen de Movimientos</h1>
                <p className="text-gray-500">
                  Vista detallada de ingresos, egresos y ventas de la sesión actual - {resumen.sesion?.nombre_caja}
                </p>
              </div>
              <button
                onClick={() => setMostrarModalRetiro(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-color-primario text-white rounded-xl text-sm font-medium hover:bg-color-secundario transition-colors shadow-md"
              >
                <FaArrowDown />
                Retiro de Caja
              </button>
            </div>

            {/* Tarjetas de Totales */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <TarjetaResumen
                titulo="Total Ingresos"
                valor={`RD$ ${formatMonto(resumen.totales?.total_ingresos)}`}
                colorBorde="border-green-500"
                colorTexto="text-green-700"
              />
              <TarjetaResumen
                titulo="Total Egresos"
                valor={`RD$ ${formatMonto(resumen.totales?.total_egresos)}`}
                colorBorde="border-red-500"
                colorTexto="text-red-700"
              />
              <TarjetaResumen
                titulo="Retiros de Caja"
                valor={`RD$ ${formatMonto(resumen.totales?.total_retiros)}`}
                colorBorde="border-purple-500"
                colorTexto="text-purple-700"
              />
              <TarjetaResumen
                titulo="Efectivo Esperado"
                valor={`RD$ ${formatMonto(resumen.totales?.efectivo_esperado)}`}
                colorBorde="border-blue-500"
                colorTexto="text-blue-700"
              />
            </div>

            {/* Ingresos por Método */}
            {resumen.ingresos?.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-300 shadow-md p-6">
                <h2 className="font-cascadia text-lg font-semibold text-gray-700 mb-4">Ingresos por Método</h2>
                <div className="flex flex-wrap gap-2">
                  {resumen.ingresos.map(i => (
                    <span
                      key={i.id_metodo_pago}
                      className="bg-green-100 text-green-700 px-3 py-2 rounded-xl text-sm border border-green-300"
                    >
                      <span className="font-semibold">{i.metodo_pago}:</span> RD$ {formatMonto(i.total)}
                      <span className="text-xs ml-1">({i.cantidad_pagos} pagos)</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Egresos */}
            {(resumen.egresos_compra?.length > 0 || resumen.egresos_gasto?.length > 0) && (
              <div className="bg-white rounded-xl border border-gray-300 shadow-md p-6">
                <h2 className="font-cascadia text-lg font-semibold text-gray-700 mb-4">Egresos</h2>
                <div className="space-y-4">
                  {resumen.egresos_compra?.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-600 mb-2">Compras</p>
                      <div className="flex flex-wrap gap-2">
                        {resumen.egresos_compra.map(e => (
                          <span
                            key={`c-${e.id_metodo_pago}`}
                            className="bg-red-100 text-red-700 px-3 py-2 rounded-xl text-sm border border-red-300"
                          >
                            <span className="font-semibold">{e.metodo_pago}:</span> RD$ {formatMonto(e.total)}
                            <span className="text-xs ml-1">({e.cantidad_pagos} pagos)</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {resumen.egresos_gasto?.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-600 mb-2">Gastos Menores</p>
                      <div className="flex flex-wrap gap-2">
                        {resumen.egresos_gasto.map(e => (
                          <span
                            key={`g-${e.id_metodo_pago}`}
                            className="bg-orange-100 text-orange-700 px-3 py-2 rounded-xl text-sm border border-orange-300"
                          >
                            <span className="font-semibold">{e.metodo_pago || 'N/A'}:</span> RD$ {formatMonto(e.total)}
                            <span className="text-xs ml-1">({e.cantidad_egresos} egresos)</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Retiros de Caja */}
            {resumen.retiros?.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-300 shadow-md p-6">
                <h2 className="font-cascadia text-lg font-semibold text-gray-700 mb-4">Retiros de Caja</h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-300">
                        <th className="text-left py-2 px-3 text-gray-600">Hora</th>
                        <th className="text-left py-2 px-3 text-gray-600">Destino</th>
                        <th className="text-left py-2 px-3 text-gray-600">Motivo</th>
                        <th className="text-right py-2 px-3 text-gray-600">Monto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resumen.retiros.map(r => (
                        <tr key={r.id_retiro_caja} className="border-b border-gray-200">
                          <td className="py-2 px-3">
                            {new Date(r.fecha_hora).toLocaleTimeString('es-DO', { hour: '2-digit', minute: '2-digit' })}
                          </td>
                          <td className="py-2 px-3">{r.nombre_destino}</td>
                          <td className="py-2 px-3 text-gray-500">{r.motivo || '-'}</td>
                          <td className="py-2 px-3 text-right font-semibold text-purple-700">
                            RD$ {formatMonto(r.monto)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Productos Vendidos por Categoría */}
            {resumen.productos_por_categoria?.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-300 shadow-md p-6">
                <h2 className="font-cascadia text-lg font-semibold text-gray-700 mb-4">Productos Vendidos por Categoría</h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-300">
                        <th className="text-left py-2 px-3 text-gray-600">Categoría</th>
                        <th className="text-right py-2 px-3 text-gray-600">Unidades</th>
                        <th className="text-right py-2 px-3 text-gray-600">Monto Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resumen.productos_por_categoria.map((p, idx) => (
                        <tr key={idx} className="border-b border-gray-200">
                          <td className="py-2 px-3">{p.categoria}</td>
                          <td className="py-2 px-3 text-right">
                            {parseFloat(p.unidades_vendidas).toLocaleString('es-DO')}
                          </td>
                          <td className="py-2 px-3 text-right">RD$ {formatMonto(p.monto_total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <ModalRetiroCaja
        mostrar={mostrarModalRetiro}
        onClose={() => setMostrarModalRetiro(false)}
        onRetiroCreado={cargarResumen}
      />
    </div>
  );
}
